import React, { ErrorInfo, ReactNode } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends (React.Component as any) {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
  }

  private handleReset = () => {
    try {
      this.setState({ hasError: false, error: null });
      window.location.reload();
    } catch (_) {
      window.location.reload();
    }
  };

  private handleClearCache = () => {
    try {
      const keysToKeep = ['grosym_user_auth', 'grosym_registered_emails'];
      Object.keys(localStorage).forEach((key) => {
        if (!keysToKeep.includes(key) && key.startsWith('grosym_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (_) {}
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen w-full bg-slate-950 text-white flex flex-col items-center justify-center p-6 select-none font-sans">
          <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl text-center flex flex-col items-center">
            <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-500 flex items-center justify-center mb-5">
              <AlertTriangle size={28} />
            </div>

            <h2 className="text-xl font-black tracking-tight text-white mb-2">
              Se recuperó la sesión
            </h2>

            <p className="text-xs text-slate-400 leading-relaxed mb-6">
              Ocurrió una interrupción temporal al cargar los componentes del lienzo. Puedes recargar la aplicación inmediatamente.
            </p>

            {this.state.error?.message && (
              <div className="w-full bg-slate-950/80 border border-slate-800/80 rounded-xl p-3 text-[11px] font-mono text-slate-400 text-left mb-6 overflow-x-auto max-h-24">
                {this.state.error.message}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
              <button
                onClick={this.handleReset}
                className="w-full sm:flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer active:scale-95"
              >
                <RefreshCw size={14} className="stroke-[2.5]" />
                <span>Recargar Aplicación</span>
              </button>

              <button
                onClick={this.handleClearCache}
                className="w-full sm:flex-1 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs transition-all border border-slate-700 flex items-center justify-center cursor-pointer active:scale-95"
              >
                <span>Restaurar Caché</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
